import { useEffect, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { useStripe, useElements, Elements, PaymentElement } from '@stripe/react-stripe-js';
import './stripe.css';
import { useNavigate } from 'react-router-dom';


const stripePublicKey = process.env.REACT_APP_STRIPE_PUBLIC_KEY;

if (!stripePublicKey) {
    console.error('Stripe public key is not configured');
}

const stripePromise = loadStripe(stripePublicKey).catch(error => {
    console.error('Failed to load Stripe:', error);
    return null;
});

const CheckoutForm = (props) => {
    const navigate = useNavigate();

    const stripe = useStripe();
    const elements = useElements();

    const [errorMessage, setErrorMessage] = useState(null);
    const [email, setEmail] = useState(null);
    const [hidePayBtn, setHidePayBtn] = useState(false);

    const VAT = 1.2;

    const sendFailureNotification = async (failedTransaction, notificationEmail) => {
        try {
            const emailData = {
                to: notificationEmail,
                subject: `Failed Transaction Alert - ${failedTransaction.custid}`,
                body: `
                    Failed Transaction Details:
                    
                    Customer ID: ${failedTransaction.custid}
                    Transaction ID: ${failedTransaction.transaction_id}
                    Stripe Payment Intent: ${failedTransaction.intent}
                    Amount: ${failedTransaction.amount} credits (£${failedTransaction.value})
                    Customer Email: ${failedTransaction.email || 'Not provided'}
                    Error: ${failedTransaction.error}
                    Timestamp: ${new Date(failedTransaction.timestamp).toLocaleString()}
                    
                    The payment was successful on Stripe but failed to record in the system.
                    Please visit /admin-recovery to manually process this transaction.
                `
            };
            
            // Send to backend endpoint
            await fetch(process.env.REACT_APP_SERVER + '/email/notify.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(emailData)
            });
        } catch (error) {
            console.error('Failed to send notification email:', error);
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        setHidePayBtn(true);

        if (!stripe || !elements) {
            setErrorMessage('Payment form not loaded. Please refresh and try again.');
            setHidePayBtn(false);
            return;
        }

        try {
            const { error, paymentIntent } = await stripe.confirmPayment({
                elements,
                redirect: 'if_required',
            });

        if (error) {
            setErrorMessage(error.message)
            setHidePayBtn(false);
            return;
        }

        if (paymentIntent.status === 'succeeded') {

            let splitBundleText = props.details.bundle.split(':');
            let amount = parseInt(splitBundleText[0].substring(1, splitBundleText[0].length));

            const requestOptions = {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    custid: props.details.custID,
                    transaction_id: props.details.transactionID,
                    intent: paymentIntent.id,
                    method: paymentIntent.payment_method,
                    value: paymentIntent.amount / 100,
                    email: email,
                    amount: amount
                })
            };

            // Retry logic with exponential backoff
            let retries = 3;
            let delay = 1000; // Start with 1 second
            let lastError;

            while (retries > 0) {
                try {
                    const response = await fetch(process.env.REACT_APP_SERVER + `/sms/transaction.php`, requestOptions);

                    if (!response.ok) {
                        throw new Error(`Transaction recording failed: ${response.status}`);
                    }

                    const result = await response.json();
                    
                    if (result.error || result.status === 'error') {
                        throw new Error(result.message || 'Transaction recording failed');
                    }

                    // Success - navigate to complete page
                    navigate('/complete', { 
                        state: { 
                            transactionId: props.details.transactionID,
                            amount: amount,
                            paymentIntentId: paymentIntent.id
                        }
                    });
                    return; // Exit function on success
                    
                } catch (error) {
                    lastError = error;
                    retries--;
                    
                    if (retries > 0) {
                        console.log(`Transaction recording failed, retrying in ${delay}ms... (${retries} retries left)`);
                        const currentDelay = delay;
                        await new Promise(resolve => setTimeout(resolve, currentDelay));
                        delay *= 2; // Exponential backoff
                    }
                }
            }

            // All retries failed
            console.error('Transaction recording error after all retries:', lastError);
            setErrorMessage(`Payment successful but credits not added. Please contact support with reference: ${paymentIntent.id}`);
            setHidePayBtn(false);
            
            // Store failed transaction details for manual recovery
            const failedTransaction = {
                timestamp: new Date().toISOString(),
                custid: props.details.custID,
                transaction_id: props.details.transactionID,
                intent: paymentIntent.id,
                method: paymentIntent.payment_method,
                value: paymentIntent.amount / 100,
                email: email,
                amount: amount,
                error: lastError.message
            };
            
            // Store in localStorage for support team
            const existingFailures = JSON.parse(localStorage.getItem('failed_transactions') || '[]');
            existingFailures.push(failedTransaction);
            localStorage.setItem('failed_transactions', JSON.stringify(existingFailures));
            
            // Send notification email if configured
            const notificationEmail = process.env.REACT_APP_NOTIFICATION_EMAIL;
            if (notificationEmail) {
                sendFailureNotification(failedTransaction, notificationEmail);
            }
        }
        } catch (err) {
            console.error('Payment confirmation error:', err);
            setErrorMessage(err.message || 'An error occurred during payment. Please try again.');
            setHidePayBtn(false);
        }
    };

    let temp = props.details.bundle.split(':')
    let bundlePrice = temp[0].substring(1, temp[0].length);
    let price = ((parseFloat(temp[1]) / 100) * parseFloat(bundlePrice)) * VAT

    return (
        <form onSubmit={handleSubmit}>
            <PaymentElement />
            <label >Email address for receipt</label>
            <input className='stripe-email-input' type='email' placeholder='example@gmail.com' onChange={e => setEmail(e.target.value)}></input>
            {errorMessage && <div style={{ color: "red", textAlign: "center" }}>{'*' + errorMessage + '*'}</div>}
            {!hidePayBtn ? <button className='stripe_submit' disabled={!stripe}>Pay &pound;{price.toFixed(2)}</button> : <div style={{display: "flex", flexDirection: "column", alignItems: "center", gap: "10px"}}>Authenticating <span className="loader"></span></div>}
        </form>
    );
};

export default function StripePayment(props) {
    const [stripeSecret, setStripeSecret] = useState(null);
    const [loadingError, setLoadingError] = useState(null);
    const [stripeLoaded, setStripeLoaded] = useState(false);

    const VAT = 1.2;

    useEffect(() => {
        // Check if Stripe loaded successfully
        stripePromise.then(stripe => {
            if (!stripe) {
                setLoadingError('Failed to load payment system. Please check your configuration.');
            } else {
                setStripeLoaded(true);
            }
        });
    }, []);

    async function fetchData() {

        let temp = props.details.bundle.split(':')
        let bundlePrice = temp[0].substring(1, temp[0].length);
        let price = ((parseFloat(temp[1]) / 100) * parseFloat(bundlePrice)) * VAT

        const secretOptions = {
            method: 'POST',
            mode: "cors",
            body: JSON.stringify({
                paymentMethodType: 'card',
                currency: 'gbp',
                amount: Math.round(price * 100),
                cust_id: props.details.custID,
                description: `${props.details.custID} - ${bundlePrice} SMS Credits`
            })
        };

        try {
            const response = await fetch(process.env.REACT_APP_SERVER + '/stripe/card.php', secretOptions);
            
            if (!response.ok) {
                throw new Error(`Failed to create payment intent: ${response.status}`);
            }
            
            const intent = await response.json();
            
            if (!intent.client_secret) {
                throw new Error('No client secret received from server');
            }
            
            setStripeSecret(intent.client_secret);
        } catch (error) {
            console.error('Error creating payment intent:', error);
            setLoadingError('Unable to initialize payment. Please try again later.');
        }

    }

    useEffect(() => {
        setStripeSecret(null);
        setLoadingError(null);
        fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.details]);

    const options = {
        // passing the client secret obtained in step 2
        clientSecret: stripeSecret,
        // Fully customizable with appearance API.
        appearance: { theme: 'stripe' }
    };

    return (
        <>
            {loadingError ? (
                <div style={{ color: 'red', textAlign: 'center' }}>{loadingError}</div>
            ) : !stripeLoaded ? (
                <div>Loading payment system...</div>
            ) : stripeSecret && stripeSecret.startsWith('pi_') ? (
                <Elements stripe={stripePromise} options={options} key={stripeSecret}>
                    <div style={{ width: "700px" }}>
                        <CheckoutForm details={props.details} />
                    </div>
                </Elements>
            ) : (
                <div>Setting up payment request. One moment.</div>
            )}
        </>
    );
};